import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

export interface MergeSegment { type: 'avatar' | 'broll'; start: number; end: number; brollIndex?: number }
interface StepLike { id: string; kind: string; title: string }

const BROLL_MAX_SECONDS = 5; // Limite de entrega dos clipes neste fluxo de e-commerce.
const SEG_COLORS: Record<string, string> = { avatar: 'bg-indigo-500', broll: 'bg-emerald-500' };

// Faixa de montagem do vídeo final: em quais trechos a IMAGEM mostra o avatar ou um
// clipe. O áudio do avatar é contínuo por baixo de tudo.
export function MergeTimeline({ segments, stepsBefore, readonly, onChange }: {
  segments: MergeSegment[];
  stepsBefore: StepLike[];
  readonly: boolean;
  onChange: (segs: MergeSegment[]) => void;
}) {
  const [editingSeg, setEditingSeg] = useState<number | null>(null);
  const [dragSeg, setDragSeg] = useState<number | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<{ startX: number; origStart: number; origEnd: number; total: number } | null>(null);

  const segs = segments || [];
  const totalDuration = segs.length ? Math.max(...segs.map(s => s.end)) : 0;
  const runwaySteps = stepsBefore.filter(s => s.kind === 'runway');
  const avatarSteps = stepsBefore.filter(s => s.kind === 'avatar_fullscreen' || s.kind === 'avatar_chromakey');
  const updateSeg = (si: number, patch: Partial<MergeSegment>) => onChange(segs.map((s, i) => i === si ? { ...s, ...patch } : s));
  const removeSeg = (si: number) => { onChange(segs.filter((_, i) => i !== si)); setEditingSeg(null); };
  const segLabel = (seg: MergeSegment) => seg.type === 'broll'
    ? (runwaySteps[seg.brollIndex ?? 0]?.title || `Clipe #${(seg.brollIndex ?? 0) + 1}`)
    : (avatarSteps.length > 1 ? (avatarSteps[seg.brollIndex ?? 0]?.title || 'Avatar') : 'Avatar');

  // Monta os trechos a partir das etapas do pipeline (clipe = 5s, avatar = 8s).
  const autoFill = () => {
    const sources = stepsBefore.filter(s => ['runway', 'avatar_fullscreen', 'avatar_chromakey'].includes(s.kind));
    if (!sources.length) { toast.error('Adicione etapas de avatar e/ou clipe antes do vídeo final.'); return; }
    let t = 0; let brollIdx = 0; let avatarIdx = 0;
    const next: MergeSegment[] = [];
    for (const s of sources) {
      if (s.kind === 'runway') { next.push({ type: 'broll', start: t, end: t + 5, brollIndex: brollIdx++ }); t += 5; }
      else { next.push({ type: 'avatar', start: t, end: t + 8, brollIndex: avatarSteps.length > 1 ? avatarIdx : undefined }); avatarIdx++; t += 8; }
    }
    onChange(next);
  };
  const addOneSeg = () => {
    const lastEnd = segs.length ? segs[segs.length - 1].end : 0;
    onChange([...segs, { type: 'avatar', start: lastEnd, end: lastEnd + 5 }]);
  };
  const dropSeg = (toIdx: number) => {
    if (dragSeg === null || dragSeg === toIdx) { setDragSeg(null); return; }
    const next = segs.map(s => ({ ...s }));
    const [moved] = next.splice(dragSeg, 1);
    next.splice(toIdx, 0, moved);
    // Recalcula start/end consecutivos preservando durações
    let t = 0;
    for (const seg of next) { const dur = seg.end - seg.start; seg.start = t; seg.end = t + dur; t += dur; }
    onChange(next);
    setDragSeg(null);
  };
  const startResize = (e: React.MouseEvent, segIdx: number, side: 'left' | 'right') => {
    if (readonly) return;
    e.preventDefault(); e.stopPropagation();
    const seg = segs[segIdx];
    resizeRef.current = { startX: e.clientX, origStart: seg.start, origEnd: seg.end, total: totalDuration || 1 };
    const onMove = (ev: MouseEvent) => {
      const r = resizeRef.current;
      if (!r || !timelineRef.current) return;
      const width = timelineRef.current.getBoundingClientRect().width || 1;
      const deltaSec = ((ev.clientX - r.startX) / width) * r.total;
      const maxDur = seg.type === 'broll' ? BROLL_MAX_SECONDS : 60;
      const updated = [...segs];
      if (side === 'right') {
        let end = Math.round((r.origEnd + deltaSec) * 2) / 2; // snap 0.5s
        end = Math.max(seg.start + 1, Math.min(end, seg.start + maxDur));
        updated[segIdx] = { ...seg, end };
      } else {
        let start = Math.round((r.origStart + deltaSec) * 2) / 2;
        start = Math.max(Math.max(0, seg.end - maxDur), Math.min(start, seg.end - 1));
        updated[segIdx] = { ...seg, start };
      }
      onChange(updated);
    };
    const onUp = () => { resizeRef.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div>
      {segs.length > 0 && totalDuration > 0 ? (
        <div>
          {/* Trilha de áudio (contínua) */}
          <div className="mb-1 flex h-6 items-center rounded-md bg-slate-200 px-2 text-[10px] font-semibold text-slate-500">🔊 Voz do avatar — contínua por {totalDuration}s</div>
          <div ref={timelineRef} className="relative flex h-14 w-full overflow-visible rounded-lg border border-slate-200 bg-slate-200">
            {segs.map((seg, si) => {
              const widthPct = ((seg.end - seg.start) / totalDuration) * 100;
              const isActive = editingSeg === si;
              return (
                <div key={si}
                  draggable={!readonly}
                  onDragStart={() => setDragSeg(si)} onDragEnd={() => setDragSeg(null)}
                  onDragOver={e => { if (dragSeg !== null) e.preventDefault(); }} onDrop={() => dropSeg(si)}
                  onClick={() => setEditingSeg(isActive ? null : si)}
                  style={{ width: `${widthPct}%` }}
                  title={`${segLabel(seg)} — ${seg.start}s → ${seg.end}s`}
                  className={`relative flex flex-col items-center justify-center select-none ${SEG_COLORS[seg.type]} text-white transition-all first:rounded-l-lg last:rounded-r-lg ${readonly ? 'cursor-pointer' : 'cursor-grab'} ${dragSeg === si ? 'opacity-40' : ''} ${isActive ? 'z-10 ring-2 ring-indigo-400 ring-offset-1 brightness-110' : 'hover:brightness-110'}`}>
                  <span className="pointer-events-none truncate px-1 text-[10px] font-bold">{seg.type === 'avatar' ? '🎙️' : '🎬'} {segLabel(seg)}</span>
                  <span className="pointer-events-none text-[9px] opacity-80">{(seg.end - seg.start).toFixed(1)}s</span>
                  {!readonly && <>
                    <div onMouseDown={e => startResize(e, si, 'left')} onClick={e => e.stopPropagation()} className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-black/20 hover:bg-black/40" />
                    <div onMouseDown={e => startResize(e, si, 'right')} onClick={e => e.stopPropagation()} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize bg-black/20 hover:bg-black/40" />
                  </>}
                </div>
              );
            })}
          </div>
          <div className="relative mt-0.5 flex text-[9px] text-slate-400">
            {segs.map((seg, si) => <span key={si} style={{ width: `${((seg.end - seg.start) / totalDuration) * 100}%` }}>{seg.start}s</span>)}
            <span className="absolute right-0">{totalDuration}s</span>
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-center text-xs text-slate-500">
          Sem trechos definidos — os clipes serão simplesmente colocados um depois do outro.
        </p>
      )}

      {editingSeg !== null && segs[editingSeg] && (() => {
        const seg = segs[editingSeg];
        return (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
            <span className="text-[11px] font-bold text-indigo-700">Trecho {editingSeg + 1}</span>
            <select value={seg.type} disabled={readonly} onChange={e => updateSeg(editingSeg, { type: e.target.value as MergeSegment['type'] })} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] font-semibold">
              <option value="avatar">🎙️ Avatar</option>
              <option value="broll">🎬 Clipe</option>
            </select>
            {seg.type === 'broll' && (
              <select value={seg.brollIndex ?? 0} disabled={readonly} onChange={e => updateSeg(editingSeg, { brollIndex: Number(e.target.value) })} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px]">
                {runwaySteps.map((rs, i) => <option key={rs.id} value={i}>{rs.title || `Clipe #${i + 1}`}</option>)}
                {runwaySteps.length === 0 && <option value={0}>Nenhum clipe no estilo</option>}
              </select>
            )}
            {seg.type === 'avatar' && avatarSteps.length > 1 && (
              <select value={seg.brollIndex ?? 0} disabled={readonly} onChange={e => updateSeg(editingSeg, { brollIndex: Number(e.target.value) })} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px]">
                {avatarSteps.map((as2, i) => <option key={as2.id} value={i}>{as2.title || `Avatar #${i + 1}`}</option>)}
              </select>
            )}
            <label className="flex items-center gap-1 text-[11px] text-slate-600">
              De <input type="number" step="0.5" min="0" disabled={readonly} value={seg.start} onChange={e => updateSeg(editingSeg, { start: Number(e.target.value) })} className="w-14 rounded border border-slate-200 px-1 py-0.5 text-center text-[11px]" />s
            </label>
            <label className="flex items-center gap-1 text-[11px] text-slate-600">
              Até <input type="number" step="0.5" min="0" disabled={readonly} value={seg.end}
                onChange={e => { const v = Number(e.target.value); updateSeg(editingSeg, { end: seg.type === 'broll' ? Math.min(v, seg.start + BROLL_MAX_SECONDS) : v }); }}
                className="w-14 rounded border border-slate-200 px-1 py-0.5 text-center text-[11px]" />s
              {seg.type === 'broll' && <span className="text-[9px] text-slate-400">(máx. 5s)</span>}
            </label>
            {!readonly && <button type="button" onClick={() => removeSeg(editingSeg)} className="ml-auto rounded bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-200">Remover trecho</button>}
          </div>
        );
      })()}

      {!readonly && (
        <div className="mt-3 flex flex-wrap gap-2">
          {segs.length === 0 ? (
            <button type="button" onClick={autoFill} className="rounded-lg border border-dashed border-indigo-300 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50">+ Montar automaticamente a partir das etapas</button>
          ) : (
            <>
              <button type="button" onClick={addOneSeg} className="rounded-lg border border-dashed border-indigo-300 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50">+ Trecho</button>
              <button type="button" onClick={autoFill} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100">↻ Remontar a partir das etapas</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
